import { useEffect } from 'react';
import { useLazyQuery } from '@common/hooks';
import { INDEX_SHIFT_CONFIGS_QUERY } from '@scheduling/graphql';
import type { IndexShiftConfigsQueryCgResponse } from '@common/graphql/schema';

export type ShiftConfigType = NonNullable<
  IndexShiftConfigsQueryCgResponse['organizationUnitShiftConfigs']
>[number];

type ShiftConfigCheckResult = {
  unitHasAnyConfigs: boolean | null;
  hasApplicableConfigs: boolean | null;
  shiftConfigs: ShiftConfigType[];
  loading: boolean;
};

export function useShiftConfigCheck({
  organizationUnitId,
  roleId,
}: {
  organizationUnitId: string | null;
  roleId: string | null;
}): ShiftConfigCheckResult {
  const [fetchUnitConfigs, { data: unitData, loading: unitLoading }] =
    useLazyQuery(INDEX_SHIFT_CONFIGS_QUERY, {
      fetchPolicy: 'cache-first',
    });

  const [fetchRoleConfigs, { data: roleData, loading: roleLoading }] =
    useLazyQuery(INDEX_SHIFT_CONFIGS_QUERY, {
      fetchPolicy: 'cache-first',
    });

  useEffect(() => {
    if (!organizationUnitId) return;

    fetchUnitConfigs({
      variables: {
        filters: { unit: organizationUnitId, skipLimitByShiftConfig: true },
      },
    });
  }, [organizationUnitId, fetchUnitConfigs]);

  useEffect(() => {
    if (!organizationUnitId || !roleId) return;

    fetchRoleConfigs({
      variables: {
        filters: {
          unit: organizationUnitId,
          role: roleId,
          skipLimitByShiftConfig: true,
        },
      },
    });
  }, [organizationUnitId, roleId, fetchRoleConfigs]);

  const unitConfigs = unitData?.organizationUnitShiftConfigs ?? [];
  const roleConfigs = roleData?.organizationUnitShiftConfigs ?? [];

  return {
    unitHasAnyConfigs: unitData ? unitConfigs.length > 0 : null,
    hasApplicableConfigs: roleData ? roleConfigs.length > 0 : null,
    shiftConfigs: roleConfigs,
    loading: unitLoading || roleLoading,
  };
}
